import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { HTTPService, HelperService, DateService, DataService } from 'app/services';
import { datetimeFormat } from 'app/constants/date-time-format';
import { TranslateService } from '@ngx-translate/core';
import { getLanguagePreferences, setLanguagePreferences } from 'app/constants/language-translate';

@Component({
  selector: 'app-appt-verify',
  templateUrl: 'appt-verify.component.html',
  styleUrls: ['./appt-verify.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ApptVerifyComponent implements OnInit {
  token: string = '';
  isLoading: boolean = false;
  isAlreadyConfirmed: boolean = false;
  verifyAppointmentView: string = 'verify';
  message: string = '';
  title: string = '';
  feedback: string = '';
  company: any = {
    name: '',
    logo: '',
    phone: '',
  };
  shop: any = {
    name: '',
    logo: '',
    phone: '',
  };
  year = new Date().getFullYear();
  status: string = 'scheduled';
  prevApptStatus: string = '';
  currentLang: string = 'en';
  companyLocalization: boolean = false;
  isLanguageDropdownVisible: boolean = false;

  constructor(private activatedRoute: ActivatedRoute, private dataService: DataService, private httpService: HTTPService, private helperService: HelperService, private dateService: DateService, private translate: TranslateService) { }

  ngOnInit(): void {
    /** fetching token and status from the url */
    this.token = this.activatedRoute.snapshot.paramMap.get('appt_conf_token');
    this.companyLocalization = this.activatedRoute.snapshot.queryParamMap.get('localization') === 'true';
    if (this.companyLocalization) {
      this.currentLang = this.activatedRoute.snapshot.queryParamMap.get('lang') || 'en';
      if (this.currentLang) this.currentLang = this.currentLang.toLowerCase();
    }
    this.translate.use(this.currentLang);
    const query: any = this.activatedRoute.queryParams;
    const status = query.value.status;
    this.prevApptStatus = query.value.status;
    /** if status is confirmed or cancelled verify the appointment on page load */
    if (status === 'confirmed' || status === 'cancelled') {
      this.processAppointment(this.token, status);
    }
  }

  toggleLanguageDropdown() {
    this.isLanguageDropdownVisible = !this.isLanguageDropdownVisible;
  }

  switchLanguage(lang: string) {
    this.translate.use(lang);
    this.currentLang = lang;
    this.isLanguageDropdownVisible = false;
    setLanguagePreferences(lang, this.companyLocalization);
    this.toggleLanguageDropdown();
    if (this.prevApptStatus === 'confirmed' || this.prevApptStatus === 'cancelled') {
      this.processAppointment(this.token, this.prevApptStatus);
    }
  }

  /**
   * Confirm or cancel the appointment
   *
   * @param token  authentication token
   * @param status status of the appointment [confirmed, cancelled]
   */
  processAppointment(token: string, status: string) {
    this.isLoading = true;

    this.httpService.verifyAppt(token, status).then(
      (res: any) => {
        if (res) {
          const { isUpdated, apptStatus, data } = res;
          const appointment = data?.appointment || {};

          this.company = data?.company || {};
          this.shop = data?.shop;

          if (this.shop && this.company.appsettings?.isMultiLocationEnabled) {
            this.company.name = this.shop.name;
            this.company.logo = this.shop.logo;
            this.company.email = this.shop.email;
            this.company.phone = this.shop.phone;
          }

          if (data.appointment.isInActive === true) {
            this.status = 'cancelled';
            if (this.currentLang === 'en') {
              this.title = `Deleted appointment`;
              this.message = ` This appointment has already been deleted by the shop. Please contact shop at
              ${this.helperService.formatPhoneNumber(this.company.phone)} to schedule another appointment `;
              return;
            } else {
              this.title = `Cita eliminada`;
              this.message = ` Esta cita ya ha sido eliminada por la tienda. Por favor, póngase en contacto con la tienda en
              ${this.helperService.formatPhoneNumber(this.company.phone)} para programar otra cita `;
              return;
            }
          }

          this.status = apptStatus;
          this.title = `${this.currentLang === "en" ? 'Appointment' : 'Rendez-vous'} ${apptStatus === 'confirmed' ? this.currentLang === 'en' ? 'confirmed' : 'confirmé' : this.currentLang === 'en' ? 'canceled' : 'annulé'}`;

          const startDateTime: any = this.dateService.getCurrentDateTimeTz(new Date(appointment.startDateTime).getTime(), this.company.timezone);
          const startDate = this.currentLang === 'fr' ? this.dateService.formatDate(new Date(startDateTime), datetimeFormat.mmmmdyyyy, 'fr') : this.dateService.formatDate(new Date(startDateTime), datetimeFormat.mmmdyyyy);

          const startTime: any = this.dateService.formatDate(new Date(startDateTime), datetimeFormat.hhmma);

          if (isUpdated) {
            if (apptStatus === 'confirmed') {
              if (this.currentLang === 'en') {
                this.message = `Thank you for confirming your appointment with ${this.company.name}
                for ${startDate} at ${startTime}.<br> See you then!`;
              } else {
                this.message = `Votre rendez-vous chez ${this.company.name}
                du ${startDate} à ${startTime} est confirmé.<br>À bientôt!`;
              }
            } else if (apptStatus === 'cancelled') {
              if (this.currentLang === 'en') {
                this.message = `We're sorry you won't be able to make it.<br><br>
                Your appointment with ${this.company.name}
                for ${startDate} at ${startTime} has been canceled.`;
              } else {
                this.message = `Nous sommes désolés que vous ne pourrez pas venir.<br><br>
                Votre rendez-vous chez ${this.company.name}
                du ${startDate} à ${startTime} a été annulé.`;
              }
            }
          } else {
            let confirmDate: any = this.dateService.getCurrentDateTimeTz(new Date(appointment.statusDate).getTime(), this.company.timezone);
            confirmDate = this.currentLang === 'fr' ? this.dateService.formatDate(new Date(confirmDate), datetimeFormat.mmmmdyyyy, 'fr') : this.dateService.formatDate(new Date(confirmDate), datetimeFormat.mmmdyyyy);
            let confirmTime: any = this.dateService.getCurrentDateTimeTz(new Date(appointment.statusDate).getTime(), this.company.timezone);
            confirmTime = this.dateService.formatDate(confirmTime, datetimeFormat.hhmma);

            if (apptStatus === 'confirmed') {
              if (this.currentLang === 'en') {
                this.message = `Thank you for confirming your appointment with ${this.company.name}
                for ${startDate} at ${startTime}.<br> See you then!`;
                this.feedback = `(You already confirmed your appointment on ${confirmDate} at ${confirmTime})`;
              } else {
                this.message = `Votre rendez-vous chez ${this.company.name}
                du ${startDate} à ${startTime} est confirmé.<br>À bientôt!`;
                this.feedback = `(Vous avez déjà confirmé votre rendez-vous le ${confirmDate} à ${confirmTime})`;
              }
            } else if (apptStatus === 'cancelled' && this.prevApptStatus === 'confirmed') {
              if (this.currentLang === 'en') {
                this.message = `You already canceled your appointment on ${confirmDate} at ${confirmTime}.
                <br></br>
                Please contact ${this.company.name} at ${this.helperService.formatPhoneNumber(this.company.phone)} for a new appointment`;
              } else {
                this.message = `Vous avez déjà annulé votre rendez-vous le ${confirmDate} à ${confirmTime}.
                <br></br>
                Veuillez contacter ${this.company.name} au ${this.helperService.formatPhoneNumber(this.company.phone)} pour un nouveau rendez-vous`;
              }
            } else {
              if (this.currentLang === 'en') {
                this.message = `We're sorry you won't be able to make it.
                <br><br>
                Your appointment with ${this.company.name} for ${startDate} at ${startTime}
                has been canceled`;
                this.feedback = `(You already canceled your appointment on ${confirmDate} at ${confirmTime})`;
              }
              else {
                this.message = `Nous sommes désolés que vous ne pourrez pas venir.<br><br>
                Votre rendez-vous chez ${this.company.name}
                du ${startDate} à ${startTime} a été annulé.`;
                this.feedback = `(Vous avez déjà annulé votre rendez-vous le ${confirmDate} à ${confirmTime})`;
              }
            }
          }
        }
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;
        this.helperService.showToaster('error', err);
      }
    );
  }

  /**
   * Verify the appointment
   *
   * @param status  status of the appointment [confirmed, cancelled]
   */
  onSubmit(status) {
    this.processAppointment(this.token, status);
  }
}
